import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Schema.org JSON-LD injection
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.blueteamcarpet.com';
    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'LocalBusiness',
          '@id': `${siteUrl}/#business`,
          name: 'Blue Team Carpet & Furnace Cleaning',
          description:
            'Professional carpet cleaning, furnace cleaning, upholstery cleaning, tile & grout cleaning, commercial cleaning, and pet odor removal services in Edmonton, AB. Open 24 hours, 7 days a week.',
          url: siteUrl,
          telephone: '+18254364945',
          email: 'blueteamedmonton@gmail.com',
          priceRange: '$$',
          address: {
            '@type': 'PostalAddress',
            streetAddress: '15903 104th Ave NW',
            addressLocality: 'Edmonton',
            addressRegion: 'AB',
            addressCountry: 'CA',
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: 53.5991,
            longitude: -113.5923,
          },
          openingHoursSpecification: [
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: [
                'Monday',
                'Tuesday',
                'Wednesday',
                'Thursday',
                'Friday',
                'Saturday',
                'Sunday',
              ],
              opens: '00:00',
              closes: '23:59',
            },
          ],
          sameAs: ['https://www.blueteamcarpet.com'],
          areaServed: [
            'Edmonton',
            'St. Albert',
            'Sherwood Park',
            'Spruce Grove',
            'Fort Saskatchewan',
            'Leduc',
            'Beaumont',
            'Stony Plain',
          ],
          hasOfferCatalog: {
            '@type': 'OfferCatalog',
            name: 'Cleaning Services',
            itemListElement: [
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: 'Carpet Cleaning',
                  description:
                    'Deep steam cleaning that removes dirt, stains, and allergens from your carpets.',
                },
              },
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: 'Furnace Cleaning',
                  description:
                    'Professional furnace and duct cleaning to improve air quality and system efficiency.',
                },
              },
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: 'Upholstery Cleaning',
                  description:
                    'Gentle yet effective cleaning for your furniture, removing stains and odors.',
                },
              },
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: 'Tile & Grout Cleaning',
                  description:
                    'Restore the original beauty of your tile floors with specialized cleaning.',
                },
              },
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: 'Commercial Cleaning',
                  description:
                    'Professional cleaning solutions for offices, retail spaces, and commercial properties.',
                },
              },
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: 'Pet Odor Removal',
                  description:
                    'Specialized treatments to eliminate pet odors and stains from carpets and upholstery.',
                },
              },
            ],
          },
        },
        {
          '@type': 'WebSite',
          '@id': `${siteUrl}/#website`,
          url: siteUrl,
          name: 'Blue Team Carpet & Furnace Cleaning',
          description:
            'Edmonton carpet cleaning, furnace cleaning, and upholstery cleaning services.',
          publisher: { '@id': `${siteUrl}/#business` },
        },
        {
          '@type': 'WebPage',
          '@id': `${siteUrl}/#webpage`,
          url: siteUrl,
          name: 'Edmonton Carpet & Furnace Cleaning | Blue Team Carpet',
          isPartOf: { '@id': `${siteUrl}/#website` },
          about: { '@id': `${siteUrl}/#business` },
          description:
            'Blue Team Carpet & Furnace Cleaning — Edmonton carpet cleaning, furnace cleaning, upholstery cleaning, and pet odor removal. Open 24/7.',
        },
      ],
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-org-jsonld';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById('schema-org-jsonld');
      if (existing) existing.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                <span className={`${isScrolled ? 'text-teal-600' : 'text-white'}`}>Blue Team</span>
              </h1>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <a href="#home" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>Home</a>
              <a href="#services" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>Services</a>
              <a href="#about" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>About</a>
              <a href="#contact" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>Contact</a>
              <a href="tel:825-436-4945" className="bg-teal-600 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap">
                825-436-4945
              </a>
            </div>

            <button 
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <i className={`ri-menu-line text-2xl ${isScrolled ? 'text-gray-700' : 'text-white'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-4 py-4 space-y-3">
              <a href="#home" className="block text-sm font-medium text-gray-700 hover:text-teal-600">Home</a>
              <a href="#services" className="block text-sm font-medium text-gray-700 hover:text-teal-600">Services</a>
              <a href="#about" className="block text-sm font-medium text-gray-700 hover:text-teal-600">About</a>
              <a href="#contact" className="block text-sm font-medium text-gray-700 hover:text-teal-600">Contact</a>
              <a href="tel:825-436-4945" className="block bg-teal-600 text-white px-6 py-2.5 rounded-lg text-sm font-medium text-center hover:bg-teal-700">
                825-436-4945
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative h-screen flex items-center justify-center">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/81dbe23472fbfb8512664e2acf5fc612.jpg"
            alt="Professional carpet cleaning services in Edmonton"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        
        <div className="relative z-10 text-center px-4 w-full max-w-5xl mx-auto">
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Professional Carpet &amp; Furnace Cleaning
          </h2>
          <p className="text-xl md:text-2xl text-white mb-8 max-w-3xl mx-auto">
            Expert cleaning services for your home and business in Edmonton, AB. We deliver exceptional results with eco-friendly solutions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#contact" className="bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap">
              Get Free Quote
            </a>
            <a href="tel:825-436-4945" className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap">
              Call Now
            </a>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Our Services
            </h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive cleaning solutions tailored to your needs in Edmonton and surrounding areas
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Carpet Cleaning */}
            <div className="bg-gray-50 rounded-xl overflow-hidden hover:shadow-xl transition-shadow">
              <div className="h-64 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20carpet%20cleaning%20technician%20using%20a%20steam%20cleaning%20machine%20on%20a%20clean%20beige%20carpet%20inside%20a%20bright%20modern%20living%20room%2C%20white%20walls%2C%20professional%20equipment%2C%20clean%20and%20tidy%20environment%2C%20high%20quality%20photography%2C%20commercial%20cleaning%20service&width=600&height=400&seq=service-carpet-001&orientation=landscape"
                  alt="Carpet cleaning services Edmonton"
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-6">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-home-smile-line text-2xl text-white"></i>
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-3">Carpet Cleaning</h4>
                <p className="text-gray-600 mb-4">
                  Deep steam cleaning that removes dirt, stains, and allergens from your carpets, leaving them fresh and revitalized.
                </p>
                <a href="#contact" className="text-teal-600 font-semibold hover:text-teal-700 inline-flex items-center">
                  Learn More <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
            </div>

            {/* Furnace Cleaning */}
            <div className="bg-gray-50 rounded-xl overflow-hidden hover:shadow-xl transition-shadow">
              <div className="h-64 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=HVAC%20technician%20cleaning%20a%20residential%20furnace%20and%20air%20duct%20system%20in%20a%20utility%20room%2C%20professional%20tools%20and%20equipment%2C%20clean%20white%20background%20walls%2C%20wearing%20uniform%2C%20professional%20home%20service%2C%20detailed%20technical%20work&width=600&height=400&seq=service-furnace-001&orientation=landscape"
                  alt="Furnace cleaning services Edmonton"
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-6">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-fire-line text-2xl text-white"></i>
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-3">Furnace Cleaning</h4>
                <p className="text-gray-600 mb-4">
                  Professional furnace and duct cleaning to improve air quality and system efficiency in your Edmonton home.
                </p>
                <a href="#contact" className="text-teal-600 font-semibold hover:text-teal-700 inline-flex items-center">
                  Learn More <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
            </div>

            {/* Upholstery Cleaning */}
            <div className="bg-gray-50 rounded-xl overflow-hidden hover:shadow-xl transition-shadow">
              <div className="h-64 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20upholstery%20cleaning%20service%20technician%20using%20steam%20cleaner%20on%20a%20light%20gray%20fabric%20sofa%20couch%20in%20a%20bright%20modern%20living%20room%2C%20clean%20white%20walls%2C%20professional%20cleaning%20equipment%2C%20before%20and%20after%20results%20visible&width=600&height=400&seq=service-upholstery-001&orientation=landscape"
                  alt="Upholstery cleaning services Edmonton"
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-6">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-sofa-line text-2xl text-white"></i>
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-3">Upholstery Cleaning</h4>
                <p className="text-gray-600 mb-4">
                  Gentle yet effective cleaning for your furniture, removing stains and odors while preserving fabric quality.
                </p>
                <a href="#contact" className="text-teal-600 font-semibold hover:text-teal-700 inline-flex items-center">
                  Learn More <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
            </div>

            {/* Tile & Grout Cleaning */}
            <div className="bg-gray-50 rounded-xl overflow-hidden hover:shadow-xl transition-shadow">
              <div className="h-64 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20tile%20and%20grout%20cleaning%20service%20showing%20sparkling%20clean%20white%20bathroom%20floor%20tiles%20with%20bright%20white%20grout%20lines%2C%20professional%20pressure%20cleaning%20equipment%20nearby%2C%20clean%20and%20pristine%20bathroom%20environment%2C%20high%20contrast%20clean%20result&width=600&height=400&seq=service-tile-001&orientation=landscape"
                  alt="Tile and grout cleaning Edmonton"
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-6">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-grid-line text-2xl text-white"></i>
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-3">Tile &amp; Grout Cleaning</h4>
                <p className="text-gray-600 mb-4">
                  Restore the original beauty of your tile floors with our specialized cleaning and sealing services.
                </p>
                <a href="#contact" className="text-teal-600 font-semibold hover:text-teal-700 inline-flex items-center">
                  Learn More <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
            </div>

            {/* Commercial Cleaning */}
            <div className="bg-gray-50 rounded-xl overflow-hidden hover:shadow-xl transition-shadow">
              <div className="h-64 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20commercial%20carpet%20cleaning%20service%20in%20a%20large%20modern%20office%20building%2C%20cleaning%20crew%20using%20industrial%20steam%20cleaning%20machines%20on%20dark%20gray%20office%20carpet%2C%20bright%20fluorescent%20lighting%2C%20open%20office%20space%20with%20desks%20in%20background&width=600&height=400&seq=service-commercial-001&orientation=landscape"
                  alt="Commercial cleaning services Edmonton"
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-6">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-building-line text-2xl text-white"></i>
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-3">Commercial Cleaning</h4>
                <p className="text-gray-600 mb-4">
                  Professional cleaning solutions for offices, retail spaces, and commercial properties in Edmonton.
                </p>
                <a href="#contact" className="text-teal-600 font-semibold hover:text-teal-700 inline-flex items-center">
                  Learn More <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
            </div>

            {/* Pet Odor Removal */}
            <div className="bg-gray-50 rounded-xl overflow-hidden hover:shadow-xl transition-shadow">
              <div className="h-64 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Pet%20odor%20removal%20and%20stain%20cleaning%20service%20showing%20a%20professional%20technician%20treating%20a%20carpet%20stain%20with%20specialized%20enzyme%20cleaner%20solution%2C%20golden%20retriever%20dog%20sitting%20nearby%20looking%20clean%2C%20bright%20cheerful%20home%20interior%2C%20white%20walls%2C%20professional%20cleaning%20service&width=600&height=400&seq=service-pet-001&orientation=landscape"
                  alt="Pet odor removal Edmonton"
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-6">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-heart-pulse-line text-2xl text-white"></i>
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-3">Pet Odor Removal</h4>
                <p className="text-gray-600 mb-4">
                  Specialized treatments to eliminate pet odors and stains, making your home fresh and clean again.
                </p>
                <a href="#contact" className="text-teal-600 font-semibold hover:text-teal-700 inline-flex items-center">
                  Learn More <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Why Choose Blue Team?
            </h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We're committed to delivering exceptional cleaning services with professionalism and care
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">Certified Professionals</h4>
              <p className="text-gray-600">
                Our team is fully trained and certified in the latest cleaning techniques and safety standards.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-leaf-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">Eco-Friendly Products</h4>
              <p className="text-gray-600">
                We use environmentally safe cleaning solutions that are tough on dirt but gentle on your home.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">Same Day Service</h4>
              <p className="text-gray-600">
                Need urgent cleaning? We offer same-day service to accommodate your schedule in Edmonton.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-money-dollar-circle-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">Affordable Pricing</h4>
              <p className="text-gray-600">
                Competitive rates with transparent pricing and no hidden fees. Quality service at fair prices.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                About Blue Team Carpet &amp; Furnace Cleaning
              </h3>
              <p className="text-lg text-gray-600 mb-6">
                With years of experience serving Edmonton and surrounding areas, Blue Team has built a reputation for excellence in carpet and furnace cleaning services. Our dedicated team of professionals is committed to providing top-quality cleaning solutions that exceed your expectations.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                We understand that your home is your sanctuary, which is why we use only the best equipment and eco-friendly cleaning products to ensure a safe, healthy environment for you and your family. From residential carpet cleaning to commercial furnace maintenance, we handle every job with the same level of care and attention to detail.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center">
                  <i className="ri-checkbox-circle-fill text-2xl text-teal-600 mr-2"></i>
                  <span className="text-gray-700 font-medium">Licensed &amp; Insured</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-checkbox-circle-fill text-2xl text-teal-600 mr-2"></i>
                  <span className="text-gray-700 font-medium">100% Satisfaction Guarantee</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-checkbox-circle-fill text-2xl text-teal-600 mr-2"></i>
                  <span className="text-gray-700 font-medium">Free Estimates</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=A%20professional%20carpet%20and%20furnace%20cleaning%20team%20of%20two%20technicians%20in%20matching%20blue%20uniforms%20smiling%20and%20posing%20with%20their%20cleaning%20equipment%20van%20outside%20a%20suburban%20house%20in%20Edmonton%20Alberta%20Canada%2C%20bright%20sunny%20day%2C%20clean%20white%20truck%20with%20equipment%2C%20friendly%20professional%20team&width=700&height=500&seq=about-team-001&orientation=landscape"
                alt="Blue Team professional cleaning team Edmonton"
                className="rounded-xl w-full h-full object-cover object-top"
                style={{ minHeight: '400px' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Service Areas
            </h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Proudly serving Edmonton and surrounding communities with professional carpet and furnace cleaning services
            </p>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4 text-center">
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="text-gray-700 font-medium">Edmonton</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="text-gray-700 font-medium">St. Albert</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="text-gray-700 font-medium">Sherwood Park</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="text-gray-700 font-medium">Spruce Grove</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="text-gray-700 font-medium">Fort Saskatchewan</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="text-gray-700 font-medium">Leduc</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="text-gray-700 font-medium">Beaumont</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="text-gray-700 font-medium">Stony Plain</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Get In Touch
            </h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Ready to experience the Blue Team difference? Contact us today for a free quote
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-phone-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Phone</h4>
                    <a href="tel:825-436-4945" className="text-lg text-teal-600 hover:text-teal-700">
                      825-436-4945
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-mail-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Email</h4>
                    <a href="mailto:blueteamedmonton@gmail.com" className="text-lg text-teal-600 hover:text-teal-700">
                      blueteamedmonton@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-map-pin-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Address</h4>
                    <p className="text-lg text-gray-600">
                      15903-104th Ave NW<br />
                      Edmonton, AB
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-global-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Website</h4>
                    <a href="https://www.blueteamcarpet.com" target="_blank" rel="noopener noreferrer" className="text-lg text-teal-600 hover:text-teal-700">
                      www.blueteamcarpet.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-time-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Business Hours</h4>
                    <p className="text-lg text-gray-600">
                      Open 24 Hours, 7 Days a Week
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2369.1234567!2d-113.5923456!3d53.5991234!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x53a0229b1f0e0001%3A0x1234567890abcdef!2s15903%20104%20Ave%20NW%2C%20Edmonton%2C%20AB!5e0!3m2!1sen!2sca!4v1234567890123!5m2!1sen!2sca"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  className="rounded-xl"
                  title="Blue Team Carpet & Furnace Cleaning Location"
                ></iframe>
              </div>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl">
              <form 
                className="space-y-6" 
                data-readdy-form 
                id="contact-form"
                onSubmit={async (e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const formData = new FormData(form);
                  const data = new URLSearchParams();
                  
                  formData.forEach((value, key) => {
                    data.append(key, value.toString());
                  });

                  try {
                    const response = await fetch('https://readdy.ai/api/form/d4kd46usn5v8oqu6pd3g', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                      },
                      body: data.toString(),
                    });

                    if (response.ok) {
                      alert('Thank you! Your message has been sent successfully. We will contact you soon.');
                      form.reset();
                    } else {
                      alert('Sorry, there was an error sending your message. Please try again or call us directly.');
                    }
                  } catch (error) {
                    alert('Sorry, there was an error sending your message. Please try again or call us directly.');
                  }
                }}
              >
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm"
                    placeholder="825-436-4945"
                  />
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                    Service Needed
                  </label>
                  <select
                    id="service"
                    name="service"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm"
                  >
                    <option value="">Select a service</option>
                    <option value="carpet-cleaning">Carpet Cleaning</option>
                    <option value="furnace-cleaning">Furnace Cleaning</option>
                    <option value="upholstery-cleaning">Upholstery Cleaning</option>
                    <option value="tile-grout-cleaning">Tile &amp; Grout Cleaning</option>
                    <option value="commercial-cleaning">Commercial Cleaning</option>
                    <option value="pet-odor-removal">Pet Odor Removal</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm resize-none"
                    placeholder="Tell us about your cleaning needs..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>

                <button
                  type="submit"
                  className="w-full bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap"
                >
                  Request Free Quote
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-teal-600 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Blue Team
              </h4>
              <p className="text-teal-100 text-sm">
                Professional carpet and furnace cleaning services in Edmonton, AB. Your trusted partner for a cleaner, healthier home.
              </p>
            </div>

            <div>
              <h5 className="text-lg font-semibold mb-4">Quick Links</h5>
              <ul className="space-y-2 text-sm">
                <li><a href="#home" className="text-teal-100 hover:text-white transition-colors">Home</a></li>
                <li><a href="#services" className="text-teal-100 hover:text-white transition-colors">Services</a></li>
                <li><a href="#about" className="text-teal-100 hover:text-white transition-colors">About</a></li>
                <li><a href="#contact" className="text-teal-100 hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>

            <div>
              <h5 className="text-lg font-semibold mb-4">Services</h5>
              <ul className="space-y-2 text-sm">
                <li><a href="#services" className="text-teal-100 hover:text-white transition-colors">Carpet Cleaning</a></li>
                <li><a href="#services" className="text-teal-100 hover:text-white transition-colors">Furnace Cleaning</a></li>
                <li><a href="#services" className="text-teal-100 hover:text-white transition-colors">Upholstery Cleaning</a></li>
                <li><a href="#services" className="text-teal-100 hover:text-white transition-colors">Commercial Cleaning</a></li>
              </ul>
            </div>

            <div>
              <h5 className="text-lg font-semibold mb-4">Contact Info</h5>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center">
                  <i className="ri-phone-line mr-2"></i>
                  <a href="tel:825-436-4945" className="text-teal-100 hover:text-white transition-colors">825-436-4945</a>
                </li>
                <li className="flex items-start">
                  <i className="ri-map-pin-line mr-2 mt-1"></i>
                  <span className="text-teal-100">15903-104th Ave NW<br />Edmonton, AB</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-global-line mr-2"></i>
                  <a href="https://www.blueteamcarpet.com" target="_blank" rel="noopener noreferrer" className="text-teal-100 hover:text-white transition-colors">www.blueteamcarpet.com</a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-teal-500 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-teal-100 text-sm mb-4 md:mb-0">
              &copy; 2024 Blue Team Carpet &amp; Furnace Cleaning. All rights reserved.
            </p>
            <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-teal-100 hover:text-white text-sm transition-colors">
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
